/**
 * RadioProvider — owns the single persistent <audio> element and the now-playing feed.
 *
 * Mounted at App level (not inside the player section) so the <audio> element
 * stays in the DOM and keeps playing while the visitor scrolls between sections
 * or navigates to /about.
 *
 * iOS Safari / Mobile WebKit Playback Pipeline (v3.2):
 *   - A single persistent <audio ref={audioRef} playsInline webkit-playsinline="true" preload="none" />
 *     is rendered here at the root provider level so route changes (/ <-> /about)
 *     never unmount or interrupt the underlying media element.
 *   - No Web Audio API (AudioContext / createMediaElementSource) or crossOrigin
 *     attributes are ever attached to the <audio> element, preventing WebKit
 *     CORS tainting / silence on live Icecast/AzuraCast streams.
 *   - play() is invoked synchronously inside the direct user click gesture handler
 *     with zero prior awaits or deferred useEffect triggers.
 *   - On pause/stop, the stream is paused and detached via removeAttribute('src')
 *     + load() so resuming later connects to the live broadcast edge.
 *   - Volume writes are wrapped in try/catch (read-only on iOS) and mute is
 *     applied directly to audio.muted.
 *   - Lock screen / Control Center integration via navigator.mediaSession.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { LOGO_URL, METADATA_POLL_MS, SITE, STREAM_URL } from './config';
import { fetchMetadata } from './api';

const RadioContext = createContext(null);

const INITIAL_METADATA = {
  artist: '',
  title: 'Loading...',
  listeners: 0,
  art: null,
  source: null,
  history: [],
};

export function RadioProvider({ children }) {
  const audioRef = useRef(null);
  const busyRef = useRef(false);
  const isPlayingRef = useRef(false);
  const isMutedRef = useRef(false);
  const volumeRef = useRef(80);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);
  const [streamError, setStreamError] = useState(null);
  const [volume, setVolumeState] = useState(80);
  const [isMuted, setIsMuted] = useState(false);
  const [metadata, setMetadata] = useState(INITIAL_METADATA);
  const [isOffline, setIsOffline] = useState(false);

  // Keep refs in sync so synchronous gesture handlers always read current state
  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  useEffect(() => {
    isMutedRef.current = isMuted;
  }, [isMuted]);

  useEffect(() => {
    volumeRef.current = volume;
  }, [volume]);

  // Ensure webkit-playsinline attribute is explicitly present for older iOS WebKit
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    try {
      audio.playsInline = true;
      audio.setAttribute('playsinline', '');
      audio.setAttribute('webkit-playsinline', 'true');
      audio.removeAttribute('crossorigin');
    } catch {
      // Ignore attribute errors on non-standard environments
    }
  }, []);

  // ------------------------------------------------------- metadata polling
  const refreshMetadata = useCallback(async () => {
    try {
      const next = await fetchMetadata();
      setMetadata(next);
      setIsOffline(next.title === 'Stream Offline');
    } catch {
      // Backend unreachable: keep the last known values rather than flicker.
      setIsOffline(true);
    }
  }, []);

  useEffect(() => {
    refreshMetadata();
    const timer = setInterval(refreshMetadata, METADATA_POLL_MS);
    return () => clearInterval(timer);
  }, [refreshMetadata]);

  // ----------------------------------------------------------- volume & mute
  // On iOS Safari, audio.volume is read-only and tied to hardware buttons.
  // Wrap volume writes in try/catch so they never throw, and always set
  // audio.muted directly since iOS honours muted state reliably.
  const applyVolumeAndMute = useCallback((nextVolume, nextMuted) => {
    const audio = audioRef.current;
    if (!audio) return;
    try {
      audio.muted = Boolean(nextMuted);
    } catch {
      // Ignore if element is not ready
    }
    try {
      const clamped = Math.max(0, Math.min(100, Number(nextVolume) || 0));
      audio.volume = nextMuted ? 0 : clamped / 100;
    } catch {
      // iOS Safari throws or ignores scripted volume writes; safe to ignore.
    }
  }, []);

  useEffect(() => {
    applyVolumeAndMute(volume, isMuted);
  }, [volume, isMuted, applyVolumeAndMute]);

  const setVolume = useCallback(
    (nextVal) => {
      const resolved = typeof nextVal === 'function' ? nextVal(volumeRef.current) : nextVal;
      const clamped = Math.max(0, Math.min(100, Number(resolved) || 0));
      volumeRef.current = clamped;
      setVolumeState(clamped);
      const nextMuted = clamped === 0 ? isMutedRef.current : false;
      if (clamped > 0 && isMutedRef.current) {
        isMutedRef.current = false;
        setIsMuted(false);
      }
      applyVolumeAndMute(clamped, nextMuted);
    },
    [applyVolumeAndMute],
  );

  const toggleMute = useCallback(() => {
    const audio = audioRef.current;
    const nextMuted = audio ? !audio.muted : !isMutedRef.current;
    isMutedRef.current = nextMuted;
    setIsMuted(nextMuted);
    if (audio) {
      try {
        audio.muted = nextMuted;
      } catch {
        // Ignore
      }
      try {
        audio.volume = nextMuted ? 0 : volumeRef.current / 100;
      } catch {
        // Read-only on iOS Safari
      }
    }
  }, []);

  // --------------------------------------------------------------- playback
  // Detach the live stream cleanly. Never assign `src = ''`: an empty string
  // resolves against the document URL and causes MEDIA_ELEMENT_ERROR on iOS.
  const detachStream = useCallback((audio) => {
    if (!audio) return;
    try {
      audio.pause();
    } catch {
      // Best-effort pause
    }
    try {
      audio.removeAttribute('src');
      audio.load();
    } catch {
      // Best-effort buffer flush
    }
  }, []);

  const stop = useCallback(() => {
    const audio = audioRef.current;
    detachStream(audio);
    isPlayingRef.current = false;
    busyRef.current = false;
    setIsPlaying(false);
    setIsLoading(false);
    setIsBuffering(false);
    try {
      if (typeof navigator !== 'undefined' && 'mediaSession' in navigator) {
        navigator.mediaSession.playbackState = 'paused';
      }
    } catch {
      // Ignore MediaSession errors
    }
  }, [detachStream]);

  const play = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || busyRef.current) return;

    busyRef.current = true;
    setStreamError(null);
    setIsLoading(true);
    setIsBuffering(true);

    // Must execute synchronously inside the user's click/tap gesture tick:
    try {
      audio.muted = isMutedRef.current;
    } catch {
      // Ignore
    }
    try {
      audio.volume = isMutedRef.current ? 0 : volumeRef.current / 100;
    } catch {
      // Read-only on iOS Safari
    }

    // Cache-busting query parameter flushes any stale edge buffer on reconnect
    const liveUrl = `${STREAM_URL}?t=${Date.now()}`;
    audio.src = liveUrl;
    audio.load();

    let playPromise;
    try {
      playPromise = audio.play();
    } catch (syncError) {
      console.error('Synchronous playback error:', syncError);
      detachStream(audio);
      isPlayingRef.current = false;
      busyRef.current = false;
      setIsPlaying(false);
      setIsLoading(false);
      setIsBuffering(false);
      setStreamError('Unable to start audio stream.');
      return;
    }

    if (playPromise && typeof playPromise.then === 'function') {
      playPromise
        .then(() => {
          isPlayingRef.current = true;
          setIsPlaying(true);
          setStreamError(null);
          try {
            if (typeof navigator !== 'undefined' && 'mediaSession' in navigator) {
              navigator.mediaSession.playbackState = 'playing';
            }
          } catch {
            // Ignore
          }
        })
        .catch((error) => {
          if (error?.name === 'AbortError') {
            // Superseded by pause or another load; media element events own state.
            return;
          }
          console.error('Playback error:', error);
          detachStream(audio);
          isPlayingRef.current = false;
          setIsPlaying(false);
          setIsBuffering(false);
          setStreamError(
            error?.name === 'NotAllowedError'
              ? 'Tap Play to start the broadcast.'
              : 'Stream signal interrupted.',
          );
        })
        .finally(() => {
          busyRef.current = false;
          setIsLoading(false);
        });
    } else {
      isPlayingRef.current = true;
      busyRef.current = false;
      setIsPlaying(true);
      setIsLoading(false);
      setIsBuffering(false);
    }
  }, [detachStream]);

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (busyRef.current) return;

    if (isPlayingRef.current || !audio.paused) {
      stop();
    } else {
      play();
    }
  }, [play, stop]);

  // ------------------------------------------------ DOM audio event listeners
  // Synchronize React state with native audio lifecycle events (lock screen,
  // Control Center, headphone unplug, incoming phone call, or dropped stream).
  // No auto-retry loop on error/stalled so a dropped stream never spins infinitely.
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return undefined;

    const onPlaying = () => {
      isPlayingRef.current = true;
      busyRef.current = false;
      setIsPlaying(true);
      setIsLoading(false);
      setIsBuffering(false);
      setStreamError(null);
    };

    const onPause = () => {
      isPlayingRef.current = false;
      busyRef.current = false;
      setIsPlaying(false);
      setIsLoading(false);
      setIsBuffering(false);
    };

    const onWaiting = () => {
      if (isPlayingRef.current) {
        setIsBuffering(true);
      }
    };

    const onStalled = () => {
      if (isPlayingRef.current) {
        setIsBuffering(true);
      }
    };

    const onEnded = () => {
      detachStream(audio);
      isPlayingRef.current = false;
      busyRef.current = false;
      setIsPlaying(false);
      setIsLoading(false);
      setIsBuffering(false);
    };

    const onError = () => {
      // Ignore spurious errors when src was intentionally removed on stop
      if (!audio.getAttribute('src')) return;
      detachStream(audio);
      isPlayingRef.current = false;
      busyRef.current = false;
      setIsPlaying(false);
      setIsLoading(false);
      setIsBuffering(false);
      setStreamError('Broadcast signal lost. Tap play to reconnect.');
    };

    audio.addEventListener('playing', onPlaying);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('waiting', onWaiting);
    audio.addEventListener('stalled', onStalled);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('error', onError);

    return () => {
      audio.removeEventListener('playing', onPlaying);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('waiting', onWaiting);
      audio.removeEventListener('stalled', onStalled);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('error', onError);
    };
  }, [detachStream]);

  // --------------------------------------------------- title presentation
  // Identical cleaning rules to v2.
  const cleanTitle = useMemo(() => {
    let fullTitle = metadata.title;
    if (metadata.artist && metadata.artist !== 'DeadMoon Radio') {
      fullTitle = `${metadata.artist} - ${metadata.title}`;
    }
    fullTitle = fullTitle.replace(/^DeadMoon\s*Radio\s*-\s*/i, '');
    fullTitle = fullTitle.replace(/^DeadMoon☾Radio\s*-\s*/i, '');
    return fullTitle || 'Unknown Track';
  }, [metadata]);

  // ------------------------------------------ Lock Screen / MediaSession API
  useEffect(() => {
    if (typeof navigator === 'undefined' || !('mediaSession' in navigator)) {
      return undefined;
    }
    try {
      if (typeof window !== 'undefined' && 'MediaMetadata' in window) {
        const artworkUrl = metadata.art || LOGO_URL;
        navigator.mediaSession.metadata = new window.MediaMetadata({
          title: metadata.title && metadata.title !== 'Loading...' ? metadata.title : SITE.name,
          artist: metadata.artist || SITE.name,
          album: SITE.name,
          artwork: artworkUrl ? [{ src: artworkUrl, sizes: '512x512', type: 'image/jpeg' }] : [],
        });
      }
    } catch {
      // Ignore MediaMetadata errors on older browsers
    }
    return undefined;
  }, [metadata.title, metadata.artist, metadata.art]);

  useEffect(() => {
    if (typeof navigator === 'undefined' || !('mediaSession' in navigator)) {
      return undefined;
    }
    const actions = [
      ['play', () => play()],
      ['pause', () => stop()],
      ['stop', () => stop()],
    ];
    actions.forEach(([action, handler]) => {
      try {
        navigator.mediaSession.setActionHandler(action, handler);
      } catch {
        // Unsupported action on this WebKit/browser build
      }
    });
    return () => {
      actions.forEach(([action]) => {
        try {
          navigator.mediaSession.setActionHandler(action, null);
        } catch {
          // Ignore cleanup errors
        }
      });
    };
  }, [play, stop]);

  const value = useMemo(
    () => ({
      audioRef,
      isPlaying,
      isLoading,
      isBuffering,
      streamError,
      isOffline,
      volume,
      isMuted,
      setVolume,
      toggleMute,
      play,
      stop,
      togglePlay,
      metadata,
      cleanTitle,
      streamUrl: STREAM_URL,
    }),
    [
      isPlaying,
      isLoading,
      isBuffering,
      streamError,
      isOffline,
      volume,
      isMuted,
      setVolume,
      toggleMute,
      play,
      stop,
      togglePlay,
      metadata,
      cleanTitle,
    ],
  );

  return (
    <RadioContext.Provider value={value}>
      {/* Single persistent <audio> element mounted at provider root so route
          navigation (/ <-> /about) never unmounts or interrupts playback.
          No crossOrigin attribute and no Web Audio API hooks (iOS Safari safe). */}
      <audio
        ref={audioRef}
        preload="none"
        playsInline
        webkit-playsinline="true"
        data-testid="audio-element"
        style={{ display: 'none' }}
      />
      {children}
    </RadioContext.Provider>
  );
}

export function useRadio() {
  const context = useContext(RadioContext);
  if (!context) throw new Error('useRadio must be used inside <RadioProvider>');
  return context;
}
